import React from "react";
import { Col, Row, Badge } from "react-bootstrap";
import { object, array, bool } from "prop-types";
import AverageScoreScale from "components/DynamicScoreReportScales/AverageScoreScale";
import { getAssessmentErrorAlert } from "../ScoreReportHelpers";

const WSP = (props) => {
  // ---------------------------- Prop Deconstruction ---------------------------
  const {
    candidateInfo,
    isInvalidTest,
    reportText,
    SelectedScoreRangeSection,
    SubcategoryScoreColumns,
    testScores,
  } = props;

  const wspTraitMap = {
    safetyPercentileScore: {
      traitName: "Safety Control Percentile",
      description:
        "The Safety Control Scale measures the degree to which an individual takes personal responsibility for their own safety at work.",
    },
    riskPercentileScore: {
      traitName: "Risk Aversion Percentile",
      description:
        "The Risk Aversion Scale provides an indication of the degree to which an individual will avoid potentially hazardous and risky behavior.",
    },
    stressPercentileScore: {
      traitName: "Stress Management Percentile",
      description:
        "The Stress Management Scale assesses the extent to which an individual manages emotional responses and tolerates various degrees of stress while at work.",
    },
    drugPercentileScore: {
      traitName: "Drug Aversion Percentile",
      description: `The Drug Aversion Scale assesses an individual's attitudes towards drug use and the likelihood that their work performance will be unaffected by drug use.`,
    },
    violencePercentileScore: {
      traitName: "Attitudes Towards Violence Percentile",
      description:
        "The Attitudes Towards Violence Scale measures the extent to which an individual is likely to avoid violent behavior within the workplace.",
    },
  };

  // ---------------------------- Results Details ----------------------------
  const getResultsDetails = () => {
    const candidateFirstName = candidateInfo.get("testTakerFirstName");
    const percentile = Number(testScores.get("percentile"));

    return (
      <Row className="gray-bottom-border pb-2 pt-3">
        <Col>
          <h3 className="reportHeader pb-1">Results Details</h3>
          <p>
            <span>
              {`In terms of safety attitudes, ${candidateFirstName}'s results indicate `}
              <strong>{reportText.get("attitude")}</strong>
              {` risk of unsafe behavior in the workplace.`}
            </span>
          </p>
          {testScores && (
            <div className="px-3 pb-2">
              <AverageScoreScale score={percentile} testId={46} />
            </div>
          )}
          {SelectedScoreRangeSection && (
            <div className="mx-3">
              <hr className="divider" />
              {SelectedScoreRangeSection}
              <hr className="divider" />
            </div>
          )}
          <p className="mt-2">{`${candidateFirstName}'s score was higher than ${percentile}% of the general population, which indicates that they are likely to:`}</p>
          <ul className="double-list">
            {reportText.get("details").map((list, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={i}>{list}</li>
            ))}
          </ul>
        </Col>
      </Row>
    );
  };

  // ---------------------------- Score Details ----------------------------
  const getScoreDetails = () => {
    const ScoreDetailSections = [];

    Object.entries(wspTraitMap).forEach(
      ([traitAbbrev, { traitName, description }]) => {
        if (testScores.get(traitAbbrev) !== null) {
          ScoreDetailSections.push(
            <Col key={traitAbbrev} lg={4}>
              <div className="score-detail trait-header">
                <span style={{ fontSize: "16px" }}>{traitName}</span>
                <span style={{ fontSize: "20px" }}>
                  {testScores.get(traitAbbrev)}
                </span>
              </div>
              <p>{description}</p>
            </Col>
          );
        }
      }
    );

    return (
      <Row className="gray-bottom-border py-3">
        <Col>
          <h3 className="reportHeader mt-1 mb-3">Score Details</h3>
          <Row>{ScoreDetailSections}</Row>
        </Col>
      </Row>
    );
  };

  // ---------------------------- Interview Questions ----------------------------
  const getInterviewQuestions = () => {
    const InterviewQuestionSections = [];

    reportText.get("interviewQuestions").forEach((questions, trait) => {
      const traitName = trait[0].toUpperCase() + trait.substring(1);
      const questionListItems = questions.map((question, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={idx}>{question}</li>
      ));

      InterviewQuestionSections.push(
        <div key={traitName} className="pb-1">
          <Badge className="badge-interview">{traitName}</Badge>
          <ul>{questionListItems}</ul>
        </div>
      );
    });

    return (
      <Row className="gray-bottom-border">
        <Col>
          <h3 className="reportHeader my-3">Interview Questions</h3>
          <Row className="pb-2">
            <Col>{InterviewQuestionSections}</Col>
          </Row>
        </Col>
      </Row>
    );
  };

  // ---------------------------- Validity and Response Style ----------------------------
  const getValidityAndResponseStyle = () => (
    <Row className="mt-3">
      <Col>
        {isInvalidTest && getAssessmentErrorAlert(46, null, testScores)}
        <h3 className="reportHeader mb-3">Validity and Response Style</h3>
        <p>
          {`The Validity and Response Style scales represent the individual's level of attention to the meaning of Workplace Safety Profile statements (Inconsistent Responding) and tendency toward positive (Self-Enhancing) self-presentation.`}
        </p>
        <Row className="px-3">{SubcategoryScoreColumns}</Row>
      </Col>
    </Row>
  );

  return reportText ? (
    <Col>
      {getResultsDetails()}
      {getScoreDetails()}
      {getInterviewQuestions()}
      {getValidityAndResponseStyle()}
    </Col>
  ) : null;
};

WSP.propTypes = {
  candidateInfo: object,
  isInvalidTest: bool,
  reportText: object,
  SelectedScoreRangeSection: object,
  SubcategoryScoreColumns: array,
  testScores: object,
};

export default WSP;
